import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import Request from "common/Request";
import ColumnsGrid from "common/layouts/ColumnsGrid";
import * as d3 from "d3";
import {
  parseProtocolsVolume,
  getFieldsOnDirection,
  getLicenseLimit,
  getMaxTotal,
  licenseRetrieval,
  protocolsVolumeRetrieval,
} from "./api";
import RangeSliderInput from "common/RangeSliderInput";
import NVStackedAreaChart from "common/graphs/NVStackedAreaChart";
import ActionsContext from "common/ActionsContext";
import BqnContext from "common/BqnContext";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import { hoursScopeChoices } from "common/constants";

const doesNothing = () => {};

const intervalForHours = (hours, interval = 10) => {
  if(hours === 24){
    return 2;
  }
  if(hours === 24 * 30){
    return 4;
  }
  if(hours === 24 * 90) {
    return 10;
  }

  return interval;
}

const _protocolsChoices = [
  { value: "none", label: "All" },
  { value: "l3proto", label: "L3-Protocol" },
  { value: "l4proto", label: "L4-Protocol" },
];

const _directionChoices = [
  { value: "all", label: "Uplink+Downlink" },
  { value: "both", label: "Both" },
  { value: "downlink", label: "Downlink" },
  { value: "uplink", label: "Uplink" },
];

const ProtocolsThroughputContextControlsLayout = styled.div`
  & .input-group {
  }
`;

const ProtocolsThroughputContextControls = ({
  hours,
  directions,
  protocols,
  className,
  onChange = doesNothing,
  children,
}) => {
  const handleChange = ({ target }) => {
    const { value, name } = target;
    switch (name) {
      case "Date Range":
        const hours = parseInt(target.value);
        onChange({ hours, interval: intervalForHours(hours) });
        break;
      case "Direction":
        onChange({ directions: value });
        break;
      case "Protocols":
        onChange({ protocols: value });
        break;
      default:
        break;
    }
  };
  return (
    <ProtocolsThroughputContextControlsLayout>
      <ColumnsGrid
        columns={6}
        className={className}
        minWidth="15rem"
        rowGap="0"
      >
        <SelectInputWithIcon
          title="Date Range"
          name="Date Range"
          icon="date_range"
          selected={hours}
          onChange={handleChange}
          options={hoursScopeChoices}
        />
        <SelectInputWithIcon
          title="Direction"
          name="Direction"
          icon="import_export"
          selected={directions}
          onChange={handleChange}
          options={_directionChoices}
        />
        <SelectInputWithIcon
          title="Protocols"
          name="Protocols"
          icon="format_line_spacing"
          selected={protocols}
          onChange={handleChange}
          options={_protocolsChoices}
        />
      </ColumnsGrid>
      {children}
    </ProtocolsThroughputContextControlsLayout>
  );
};

const calcTimeRange = (items) => {
  const [from, to] = d3.extent(items.map((item) => item.time));
  return { from, to };
};

const BigGraph = styled.div`
  & {
    display: flex;
    &.big {
      div,
      svg {
        height: 15cm !important;
        width: 100%;
      }
    }
    &.split {
      div,
      svg {
        width: 100%;
      }
    }
  }
  @media print {
    width: 100% !important;
    overflow: hidden;
  }
`;

const UsageGraphLayout = styled.div`
  h4 {
    text-align: center;
  }
`;

const SliderWrapper = styled.div`
  margin-top: ${(props) => props.margin}px;
`;

const threeDecimals = d3.format(".3f");

const UsageGraph = ({
  fields,
  thresholds,
  items,
  range,
  splitted = false,
  title = null,
}) => (
  <UsageGraphLayout className="group margin-t-15">
    {title === null ? null : (
      <h4 className="chart-title" style={{ marginTop: 20 }}>
        {title}
      </h4>
    )}
    <BigGraph className={`${splitted ? "split" : "big"} graph`}>
      <NVStackedAreaChart
        fields={fields}
        items={items.filter(isInsideRange(range))}
        thresholds={thresholds}
        xAxisFormat={d3.timeFormat("%m/%d %H:%M")}
        xField="time"
        yTooltipFormat={(value) => `${threeDecimals(value)} Mbps`}
        yAxisUnits="Mbps"
        yAxisTopGap={0.05}
      />
    </BigGraph>
  </UsageGraphLayout>
);

const isInsideRange =
  ({ from, to }, field = "time") =>
  (item) =>
    from <= item[field] && item[field] <= to;

const getMarginSlider = (directions)=>{
  if(directions==="both"){
    return 0;
  }
  if(directions==="all"){
    return 70;
  }
  return 50;
  }

const ProtocolsThroughputResult = ({ items, groups, directions, ...rest }) => {
  const marginSlider = getMarginSlider(directions);
  const initialRange = calcTimeRange(items);
  const expandRange = () => {
    setRange(calcTimeRange(items));
  };
  const [range, setRange] = useState(null);
  const applyRange = (newRange) => setRange(newRange);
  useEffect(expandRange, [items]);
  return range === null ? null : (
    <>
      {[
        ...groups.map(({ fields, directions, thresholds, title = null }) => (
          <UsageGraph
            key={directions}
            fields={fields}
            items={items}
            thresholds={thresholds}
            range={range}
            splitted={groups.length > 1}
            title={title}
            {...rest}
          />
        )),
        null,
      ]}
      <SliderWrapper margin={marginSlider}>
        <RangeSliderInput
          {...initialRange}
          className="full-width"
          onChange={applyRange}
        />
      </SliderWrapper>
    </>
  );
};

const hourlyInteval = (hours) =>
  hours === 24 ? 5 : hours === 24 * 30 ? 60 : 10;

const doVolumeRetrieval = ({hours = 72, protocols = "none", mode, bqnId}) =>
  protocolsVolumeRetrieval({
    hours,
    interval: hourlyInteval(hours),
    protocols,
    mode,
    bqnId,
  })
    .then((response) => {
      return { response };
    });

const retrieval = ({ hours, protocols, mode, bqnId }) =>
  Promise.all([
    doVolumeRetrieval({hours, protocols, mode, bqnId}),
    requestLicenseLimit({mode, bqnId}),
  ]).then(([responseVolume, responseLimit]) => {
    return { responseVolume: responseVolume.response, responseLimit };
  });

const requestLicenseLimit = ({mode, bqnId}) =>
  licenseRetrieval(mode, bqnId).then((response)=> getLicenseLimit(response, mode, bqnId));

const getFieldsItemsThresholds = (response, directions, limit, bqnId) => {
  const items = parseProtocolsVolume(response);
  if (directions === "both") {
    return {
      items,
      groups: [
        {
          title: "Downlink",
          directions: "downlink",
          fields: getFieldsOnDirection("downlink")(response),
        },
        {
          title: "Uplink",
          directions: "uplink",
          fields: getFieldsOnDirection("uplink")(response),
        },
      ],
    };
  }
  if (directions === "all") {
    const thresholds =  bqnId === "all" || limit === null || limit > getMaxTotal(items) * 1.2
    ? []
    : [{ value: limit, label: "License" }];
    return {
      items,
      groups: [
        {
          directions,
          thresholds: thresholds,
          fields: getFieldsOnDirection(directions)(response),
        },
      ],
    };
  }
  return {
    items,
    groups: [
      { directions, fields: getFieldsOnDirection(directions)(response) },
    ],
  };
};

const _defaultContext = {
  hours: 24,
  protocols: "none",
};

const _defaultLocalContext = {
  directions: "both",
};

const ProtocolsThroughputRequest = ({
  hours,
  protocols,
  directions,
  ...rest
}) => {
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;
  const actions = useContext(ActionsContext);
  const [request, setRequest] = useState(null);
  const doLoad = () => {
    setRequest(retrieval({ hours, protocols, mode, bqnId }));
  };
  // Nothing is asked while the BQN context has not loaded the BQN selected:
  // the commands would not carry its option, and their responses would be
  // those of the whole cluster. The effect runs again when it does.
  useEffect(
    () =>
      bqnId === undefined
        ? undefined
        : doLoad() || actions.recv("do-load", () => doLoad()),
    [hours, protocols, mode, bqnId]
  );
  return request === null ? null : (
    <Request during={request}>
      {({ responseVolume, responseLimit }) => {
        const result = getFieldsItemsThresholds(
          responseVolume,
          directions,
          responseLimit,
          bqnId
        );
        return <ProtocolsThroughputResult directions={directions} {...result} {...rest} />;
      }}
    </Request>
  );
};

const ProtocolsThroughputContext = () => {
  const [context, setContext] = useState(_defaultContext);
  const [localContext, setLocalContext] = useState(_defaultLocalContext);
  const updateContext = ({ hours, protocols, directions, ...context }) =>
    directions === undefined
      ? setContext((prev) => ({
          ...prev,
          ...(hours === undefined ? {} : { hours }),
          ...(protocols === undefined ? {} : { protocols }),
          ...context,
        }))
      : setLocalContext((prev) => ({
          ...prev,
          ...{ directions },
        }));

  return (
    <ProtocolsThroughputContextControls
      {...context}
      {...localContext}
      onChange={updateContext}
    >
      <ProtocolsThroughputRequest {...context} {...localContext} />
    </ProtocolsThroughputContextControls>
  );
};
export default ProtocolsThroughputContext;
